import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { visaApplicationsAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import MessagesPanel from '../components/MessagesPanel';
import './VisaApplications.css';

const VisaApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    draft: 0,
    pending_payment: 0,
    submitted: 0,
    in_review: 0,
    documents_required: 0,
    processing: 0,
    approved: 0,
    rejected: 0,
    cancelled: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    country: '',
    agency_id: '',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [agencyDropdownOpen, setAgencyDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [countries, setCountries] = useState([]);
  const [agencies, setAgencies] = useState([]);

  const filterDropdownRef = useRef(null);
  const countryDropdownRef = useRef(null);
  const agencyDropdownRef = useRef(null);
  const statusDropdownRefs = useRef({});

  // Detail Modal State
  const [detailModal, setDetailModal] = useState({
    isOpen: false,
    application: null,
    loading: false,
    error: null
  });
  const [photoUrl, setPhotoUrl] = useState(null);
  const [chatAppId, setChatAppId] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [emailModal, setEmailModal] = useState({ isOpen: false, subject: '', body: '', sending: false, appId: null, appEmail: '' });
  const grantedVisaInputRef = useRef(null);
  const [uploadingVisa, setUploadingVisa] = useState(false);

  // Status change confirmation modal state
  const [statusConfirm, setStatusConfirm] = useState(null); // { appId, newStatus, currentStatus, appNumber }

  // WhatsApp confirmation modal state
  const [whatsAppConfirm, setWhatsAppConfirm] = useState(null); // { appId, newStatus, applicantName }

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await visaApplicationsAPI.getAll(filters);
      if (response.data.success) {
        setApplications(response.data.data.applications);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = async () => {
    try {
      const response = await visaApplicationsAPI.getStats();
      if (response.data.success) {
        setStats(response.data.data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchFiltersData = async () => {
    try {
      const [countriesRes, agenciesRes] = await Promise.all([
        visaApplicationsAPI.getCountries(),
        visaApplicationsAPI.getAgencies()
      ]);
      if (countriesRes.data.success) {
        setCountries(countriesRes.data.data.countries);
      }
      if (agenciesRes.data.success) {
        setAgencies(agenciesRes.data.data.agencies);
      }
    } catch (error) {
      console.error('Failed to fetch filter data:', error);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  useEffect(() => {
    fetchStats();
    fetchFiltersData();
  }, []);

  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setFilterDropdownOpen(false);
      }
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setCountryDropdownOpen(false);
      }
      if (agencyDropdownRef.current && !agencyDropdownRef.current.contains(event.target)) {
        setAgencyDropdownOpen(false);
      }
      if (statusDropdownOpen !== null) {
        const currentRef = statusDropdownRefs.current[statusDropdownOpen];
        const portalMenu = document.querySelector('.status-dropdown-menu');
        if (currentRef && !currentRef.contains(event.target) && (!portalMenu || !portalMenu.contains(event.target))) {
          setStatusDropdownOpen(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusDropdownOpen]);

  const handleSearch = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }));
  };

  const handleStatusFilter = (status) => {
    setFilters(prev => ({ ...prev, status: prev.status === status ? '' : status, page: 1 }));
  };

  const handleCountryFilter = (country) => {
    setFilters(prev => ({ ...prev, country: prev.country === country ? '' : country, page: 1 }));
    setCountryDropdownOpen(false);
  };

  const handleAgencyFilter = (agencyId) => {
    setFilters(prev => ({ ...prev, agency_id: prev.agency_id === agencyId ? '' : agencyId, page: 1 }));
    setAgencyDropdownOpen(false);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters(prev => ({ ...prev, page: newPage }));
    }
  };

  // Step 1: Dropdown click → show status confirmation
  const requestStatusChange = (id, newStatus) => {
    const app = applications.find(a => a.id === id);
    if (app.status === newStatus) return;
    const name = app?.user_name || (app?.first_name ? `${app.first_name} ${app.last_name || ''}`.trim() : 'Applicant');
    setStatusConfirm({ appId: id, newStatus, currentStatus: app.status, appNumber: app.application_number, applicantName: name });
    setStatusDropdownOpen(null);
  };

  // Step 2: Status confirmed → if approved/rejected show WhatsApp modal, else execute
  const confirmStatusChange = () => {
    const { appId, newStatus, applicantName } = statusConfirm;
    setStatusConfirm(null);
    if (newStatus === 'approved' || newStatus === 'rejected') {
      setWhatsAppConfirm({ appId, newStatus, applicantName });
    } else {
      executeStatusChange(appId, newStatus, false);
    }
  };

  // Step 3: Execute the actual API call
  const executeStatusChange = async (id, newStatus, sendWhatsApp = false) => {
    try {
      await visaApplicationsAPI.updateStatus(id, { status: newStatus, sendWhatsApp });
      fetchApplications();
      fetchStats();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
    setWhatsAppConfirm(null);
  };

  const handleAssignAgency = async (appId, agencyId) => {
    try {
      await visaApplicationsAPI.assignToAgency(appId, { agencyId });
      fetchApplications();
    } catch (error) {
      console.error('Failed to assign agency:', error);
      alert('Failed to assign agency. Please try again.');
    }
  };

  const handleDeleteApplication = async (app) => {
    if (!window.confirm(`Delete application ${app.application_number}? This action can be undone by restoring from database.`)) {
      return;
    }
    try {
      await visaApplicationsAPI.delete(app.id);
      fetchApplications();
      fetchStats();
    } catch (error) {
      console.error('Failed to delete application:', error);
      alert('Failed to delete application. Please try again.');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !detailModal.application) return;
    setCommentLoading(true);
    try {
      const response = await visaApplicationsAPI.addComment(detailModal.application.id, newComment.trim());
      if (response.data.success) {
        setDetailModal(prev => ({
          ...prev,
          comments: [response.data.data.comment, ...(prev.comments || [])]
        }));
        setNewComment('');
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
      alert('Failed to add comment');
    } finally {
      setCommentLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!detailModal.application) return;
    try {
      await visaApplicationsAPI.deleteComment(detailModal.application.id, commentId);
      setDetailModal(prev => ({
        ...prev,
        comments: (prev.comments || []).filter(c => c.id !== commentId)
      }));
    } catch (error) {
      console.error('Failed to delete comment:', error);
      alert(error.response?.data?.message || 'Failed to delete comment');
    }
  };

  const handleSendEmail = async () => {
    if (!emailModal.subject.trim() || !emailModal.body.trim() || !emailModal.appId) return;
    setEmailModal(prev => ({ ...prev, sending: true }));
    try {
      const response = await visaApplicationsAPI.sendEmail(emailModal.appId, {
        subject: emailModal.subject.trim(),
        body: emailModal.body.trim()
      });
      if (response.data.success) {
        alert(response.data.message);
        setEmailModal({ isOpen: false, subject: '', body: '', sending: false, appId: null, appEmail: '' });
      }
    } catch (error) {
      console.error('Failed to send email:', error);
      alert('Failed to send email');
      setEmailModal(prev => ({ ...prev, sending: false }));
    }
  };

  const handleViewDetails = async (id) => {
    try {
      setDetailModal({ isOpen: true, application: null, loading: true, error: null });
      if (photoUrl) { URL.revokeObjectURL(photoUrl); setPhotoUrl(null); }
      const response = await visaApplicationsAPI.getById(id);
      if (response.data.success) {
        const photo = response.data.data.processedPhoto || null;
        setDetailModal({
          isOpen: true,
          application: response.data.data.application,
          processedPhoto: photo,
          transactions: response.data.data.transactions || [],
          statusHistory: response.data.data.statusHistory || [],
          documents: response.data.data.documents || [],
          groupTravelers: response.data.data.groupTravelers || [],
          comments: response.data.data.comments || [],
          loading: false,
          error: null
        });
        // Load photo via authenticated fetch
        if (photo) {
          try {
            const photoRes = await fetch(`${process.env.REACT_APP_API_URL}/visa-applications/photos/${photo.id}/view`, { credentials: 'include' });
            if (photoRes.ok) {
              const blob = await photoRes.blob();
              setPhotoUrl(URL.createObjectURL(blob));
            }
          } catch (_) {}
        }
      } else {
        setDetailModal({ isOpen: true, application: null, loading: false, error: 'Failed to load application details' });
      }
    } catch (error) {
      console.error('Failed to fetch application details:', error);
      setDetailModal({ isOpen: true, application: null, loading: false, error: error.message || 'Failed to load application details' });
    }
  };

  const closeDetailModal = () => {
    setDetailModal({ isOpen: false, application: null, loading: false, error: null });
    if (photoUrl) { URL.revokeObjectURL(photoUrl); setPhotoUrl(null); }
  };

  const handleGrantedVisaSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = ''; // allow re-selecting the same file later
    if (!file || !detailModal.application) return;
    setUploadingVisa(true);
    try {
      const res = await visaApplicationsAPI.uploadGrantedVisa(detailModal.application.id, file);
      if (res.data && res.data.success) {
        const n = (res.data.data && res.data.data.notified) || {};
        alert(
          `Granted visa uploaded.\n\nApplicant notified:\n• Email: ${n.email ? 'sent' : 'not sent'}\n• WhatsApp: ${n.whatsapp ? 'sent' : 'not sent'}` +
          (n.errors && n.errors.length ? `\n\nNotes: ${n.errors.join('; ')}` : '')
        );
        await handleViewDetails(detailModal.application.id); // refresh so the doc appears
      } else {
        alert((res.data && res.data.message) || 'Upload failed');
      }
    } catch (err) {
      alert((err.response && err.response.data && err.response.data.message) || err.message || 'Upload failed');
    } finally {
      setUploadingVisa(false);
    }
  };

  const handleViewDocument = async (documentId) => {
    try {
      const response = await visaApplicationsAPI.getDocumentViewUrl(documentId);
      if (response.data.success && response.data.data.viewUrl) {
        window.open(response.data.data.viewUrl, '_blank', 'noopener,noreferrer');
      } else {
        alert('Document URL not available');
      }
    } catch (error) {
      console.error('Failed to get document URL:', error);
      alert('Failed to open document. Please try again.');
    }
  };

  const handleDownloadDocument = async (documentId, fileName) => {
    try {
      const response = await visaApplicationsAPI.getDocumentViewUrl(documentId);
      if (response.data.success && response.data.data.viewUrl) {
        const res = await fetch(response.data.data.viewUrl);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || `document-${documentId}`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        alert('Document URL not available');
      }
    } catch (error) {
      console.error('Failed to download document:', error);
      alert('Failed to download document. Please try again.');
    }
  };

  const statusOptions = [
    { value: 'draft', label: 'DRAFT', className: 'status-draft' },
    { value: 'pending_payment', label: 'PENDING PAYMENT', className: 'status-pending-payment' },
    { value: 'submitted', label: 'SUBMITTED', className: 'status-submitted' },
    { value: 'in_review', label: 'IN REVIEW', className: 'status-in-review' },
    { value: 'documents_required', label: 'DOCS REQUIRED', className: 'status-documents-required' },
    { value: 'processing', label: 'PROCESSING', className: 'status-processing' },
    { value: 'approved', label: 'APPROVED', className: 'status-approved' },
    { value: 'rejected', label: 'REJECTED', className: 'status-rejected' },
    { value: 'cancelled', label: 'CANCELLED', className: 'status-cancelled' }
  ];

  const filterOptions = [
    { value: '', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'pending_payment', label: 'Pending Payment' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'in_review', label: 'In Review' },
    { value: 'documents_required', label: 'Docs Required' },
    { value: 'processing', label: 'Processing' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const getStatusConfig = (status) => {
    return statusOptions.find(opt => opt.value === status) || statusOptions[0];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return 'JUST NOW';
    if (hours < 24) return `${hours}H AGO`;
    return `${days}D AGO`;
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">VISA APPLICATIONS</h1>
            <p className="page-subtitle">APPLICATION TRACKING CENTER</p>
          </div>
          <div className="header-right">
            <div className="search-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search applications..."
                value={filters.search}
                onChange={handleSearch}
              />
            </div>
            <div className="custom-dropdown" ref={filterDropdownRef}>
              <button
                className="custom-dropdown-trigger"
                onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
              >
                <span>{filterOptions.find(opt => opt.value === filters.status)?.label || 'All Status'}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {filterDropdownOpen && (
                <div className="custom-dropdown-menu">
                  {filterOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`custom-dropdown-item ${filters.status === option.value ? 'active' : ''}`}
                      onClick={() => {
                        handleStatusFilter(option.value);
                        setFilterDropdownOpen(false);
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="custom-dropdown" ref={countryDropdownRef}>
              <button
                className="custom-dropdown-trigger"
                onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
              >
                <span>{filters.country || 'All Countries'}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {countryDropdownOpen && (
                <div className="custom-dropdown-menu custom-dropdown-menu-scroll">
                  <div
                    className={`custom-dropdown-item ${filters.country === '' ? 'active' : ''}`}
                    onClick={() => handleCountryFilter('')}
                  >
                    All Countries
                  </div>
                  {countries.map((country) => (
                    <div
                      key={country.name}
                      className={`custom-dropdown-item ${filters.country === country.name ? 'active' : ''}`}
                      onClick={() => handleCountryFilter(country.name)}
                    >
                      {country.name} ({country.count})
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="custom-dropdown" ref={agencyDropdownRef}>
              <button
                className="custom-dropdown-trigger"
                onClick={() => setAgencyDropdownOpen(!agencyDropdownOpen)}
              >
                <span>{agencies.find(a => a.id === parseInt(filters.agency_id))?.agency_name || 'All Agencies'}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {agencyDropdownOpen && (
                <div className="custom-dropdown-menu custom-dropdown-menu-scroll">
                  <div
                    className={`custom-dropdown-item ${filters.agency_id === '' ? 'active' : ''}`}
                    onClick={() => handleAgencyFilter('')}
                  >
                    All Agencies
                  </div>
                  {agencies.map((agency) => (
                    <div
                      key={agency.id}
                      className={`custom-dropdown-item ${filters.agency_id === agency.id.toString() ? 'active' : ''}`}
                      onClick={() => handleAgencyFilter(agency.id.toString())}
                    >
                      {agency.agency_name} ({agency.count})
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="dashboard-content">
          {/* Stats Cards */}
          <div className="stats-grid stats-grid-5">
            <div
              className={`stat-card stat-card-clickable ${filters.status === '' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('')}
            >
              <div className="stat-info">
                <span className="stat-label">TOTAL</span>
                <span className="stat-value">{stats.total}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-primary stat-card-clickable ${filters.status === 'submitted' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('submitted')}
            >
              <div className="stat-info">
                <span className="stat-label">SUBMITTED</span>
                <span className="stat-value">{stats.submitted}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-warning stat-card-clickable ${filters.status === 'in_review' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('in_review')}
            >
              <div className="stat-info">
                <span className="stat-label">IN REVIEW</span>
                <span className="stat-value">{stats.in_review}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-success stat-card-clickable ${filters.status === 'approved' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('approved')}
            >
              <div className="stat-info">
                <span className="stat-label">APPROVED</span>
                <span className="stat-value">{stats.approved}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-error stat-card-clickable ${filters.status === 'rejected' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('rejected')}
            >
              <div className="stat-info">
                <span className="stat-label">REJECTED</span>
                <span className="stat-value">{stats.rejected}</span>
              </div>
            </div>
          </div>

          {/* Applications Table */}
          <div className="dashboard-section applications-table-section">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading applications...</p>
              </div>
            ) : applications.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                <p>No applications found</p>
              </div>
            ) : (
              <>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>APPLICATION</th>
                        <th>APPLICANT</th>
                        <th>VISA TYPE</th>
                        <th>AGENCY</th>
                        <th>AMOUNT</th>
                        <th>STATUS</th>
                        <th>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map((app) => (
                        <tr key={app.id}>
                          <td>
                            <div className="application-info">
                              <div className="application-avatar">
                                {app.country_name?.charAt(0).toUpperCase() || 'V'}
                              </div>
                              <div className="application-details">
                                <span className="application-number">{app.application_number}</span>
                                <span className="application-date">{formatDate(app.created_at)}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="applicant-info">
                              <span className="applicant-name">{app.user_name || (app.first_name || app.last_name ? `${app.first_name || ''} ${app.last_name || ''}`.trim() : app.user_email || app.email || '-')}</span>
                              <span className="applicant-email">{app.user_email || app.email}</span>
                            </div>
                          </td>
                          <td>
                            <div className="visa-info">
                              <span className="visa-country">{app.country_name}</span>
                              <span className="visa-type">{app.visa_name}</span>
                            </div>
                          </td>
                          <td>
                            <div className="agency-info">
                              <select
                                className={`agency-assign-select${app.agency_name ? ' agency-assigned' : ''}`}
                                value={app.agency_id || ''}
                                onChange={(e) => {
                                  if (e.target.value) handleAssignAgency(app.id, e.target.value);
                                }}
                              >
                                <option value="">Assign Agency</option>
                                {agencies.map(a => (
                                  <option key={a.id} value={a.id}>{a.agency_name}</option>
                                ))}
                              </select>
                            </div>
                          </td>
                          <td>
                            <div className="amount-info">
                              <span className="amount-value">{formatCurrency(app.total_amount, app.currency)}</span>
                              <span className="payment-status payment-status-{app.payment_status}">{app.payment_status || 'pending'}</span>
                            </div>
                          </td>
                          <td>
                            <div className="status-cell">
                              <div
                                className="status-custom-dropdown"
                                ref={el => statusDropdownRefs.current[app.id] = el}
                              >
                                <button
                                  className={`status-dropdown-trigger status-dropdown-${app.status}`}
                                  onClick={(e) => {
                                    if (statusDropdownOpen === app.id) {
                                      setStatusDropdownOpen(null);
                                    } else {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const spaceBelow = window.innerHeight - rect.bottom;
                                      const menuHeight = statusOptions.length * 32 + 8;
                                      const openUpward = spaceBelow < menuHeight;
                                      setDropdownPosition({
                                        top: openUpward ? rect.top - menuHeight : rect.bottom + 4,
                                        left: rect.left
                                      });
                                      setStatusDropdownOpen(app.id);
                                    }
                                  }}
                                >
                                  <span>{getStatusConfig(app.status).label}</span>
                                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                    <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                                {statusDropdownOpen === app.id && ReactDOM.createPortal(
                                  <div
                                    className="status-dropdown-menu"
                                    style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                                  >
                                    {statusOptions.map((option) => (
                                      <div
                                        key={option.value}
                                        className={`status-dropdown-item ${app.status === option.value ? 'active' : ''}`}
                                        onClick={() => {
                                          requestStatusChange(app.id, option.value);
                                          setStatusDropdownOpen(null);
                                        }}
                                      >
                                        {option.label}
                                      </div>
                                    ))}
                                  </div>,
                                  document.body
                                )}
                              </div>
                              <span className="status-time">{formatRelativeTime(app.updated_at)}</span>
                            </div>
                          </td>
                          <td>
                            <div className="actions-cell">
                              <button
                                className="btn-view-application"
                                onClick={() => handleViewDetails(app.id)}
                              >
                                VIEW
                              </button>
                              <button
                                className="btn-chat-application"
                                onClick={() => setChatAppId(app.id)}
                                title="WhatsApp Chat"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                                </svg>
                              </button>
                              <button
                                className="btn-email-application"
                                onClick={() => setEmailModal({ isOpen: true, subject: '', body: '', sending: false, appId: app.id, appEmail: app.user_email || app.email || '' })}
                                title="Send Email"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                  <polyline points="22,6 12,13 2,6"></polyline>
                                </svg>
                              </button>
                              <button
                                className="btn-delete-application"
                                onClick={() => handleDeleteApplication(app)}
                                title="Delete application"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"></polyline>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="table-footer">
                  <div className="pagination-info">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                    {pagination.total} applications
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="pagination-btn"
                      disabled={pagination.page === 1}
                      onClick={() => handlePageChange(pagination.page - 1)}
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          className={`pagination-btn ${pagination.page === pageNum ? 'active' : ''}`}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      className="pagination-btn"
                      disabled={pagination.page === pagination.totalPages}
                      onClick={() => handlePageChange(pagination.page + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Chat Drawer */}
      {chatAppId && (
        <div className="chat-drawer-overlay" onClick={() => setChatAppId(null)}>
          <div className="chat-drawer" onClick={(e) => e.stopPropagation()}>
            <MessagesPanel applicationId={chatAppId} onClose={() => setChatAppId(null)} />
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailModal.isOpen && (
        <div className="modal-overlay" onClick={closeDetailModal}>
          <div className="modal-content detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Application Details</h2>
              <div className="modal-header-actions">
                {detailModal.application && (
                  <button className="email-applicant-btn" onClick={() => setEmailModal({ isOpen: true, subject: '', body: '', sending: false, appId: detailModal.application.id, appEmail: detailModal.application.user_email || '' })}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                    Email
                  </button>
                )}
                {detailModal.application && (
                  <>
                    <input
                      type="file"
                      ref={grantedVisaInputRef}
                      accept="application/pdf,image/jpeg,image/png,image/webp"
                      style={{ display: 'none' }}
                      onChange={handleGrantedVisaSelected}
                    />
                    <button
                      className="email-applicant-btn"
                      disabled={uploadingVisa}
                      onClick={() => grantedVisaInputRef.current && grantedVisaInputRef.current.click()}
                      title="Upload the issued visa — it goes to the applicant's vault and notifies them by email + WhatsApp"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="17 8 12 3 7 8"></polyline>
                        <line x1="12" y1="3" x2="12" y2="15"></line>
                      </svg>
                      {uploadingVisa ? 'Uploading…' : 'Upload granted visa'}
                    </button>
                  </>
                )}
                <button className="modal-close" onClick={closeDetailModal}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            </div>

            {detailModal.loading ? (
              <div className="modal-body loading-state">
                <div className="spinner"></div>
                <p>Loading details...</p>
              </div>
            ) : detailModal.error ? (
              <div className="modal-body loading-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--color-error, #ef4444)" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <p style={{ color: 'var(--color-error, #ef4444)' }}>{detailModal.error}</p>
                <button className="btn-view-application" onClick={closeDetailModal} style={{ marginTop: '16px' }}>
                  Close
                </button>
              </div>
            ) : detailModal.application && (
              <div className="modal-body">
                <div className="detail-section">
                  <h3 className="detail-section-title">Application Info</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Application Number</span>
                      <span className="detail-value">{detailModal.application.application_number}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Status</span>
                      <span className={`detail-status status-badge-${detailModal.application.status}`}>
                        {getStatusConfig(detailModal.application.status).label}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Created</span>
                      <span className="detail-value">{formatDate(detailModal.application.created_at)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Last Updated</span>
                      <span className="detail-value">{formatDate(detailModal.application.updated_at)}</span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3 className="detail-section-title">Applicant Info</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Full Name</span>
                      <span className="detail-value">{detailModal.application.user_name || (detailModal.application.first_name || detailModal.application.last_name ? `${detailModal.application.first_name || ''} ${detailModal.application.last_name || ''}`.trim() : '-')}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Email</span>
                      <span className="detail-value">{detailModal.application.user_email || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Phone</span>
                      <span className="detail-value">{detailModal.application.user_phone || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">WhatsApp</span>
                      <span className="detail-value">{detailModal.application.whatsapp_number || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Nationality</span>
                      <span className="detail-value">{detailModal.application.nationality || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Date of Birth</span>
                      <span className="detail-value">{formatDate(detailModal.application.date_of_birth)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Gender</span>
                      <span className="detail-value">{detailModal.application.gender ? detailModal.application.gender.charAt(0).toUpperCase() + detailModal.application.gender.slice(1) : '-'}</span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3 className="detail-section-title">Visa Info</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Destination Country</span>
                      <span className="detail-value">{detailModal.application.country_flag} {detailModal.application.country_name}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Visa Type</span>
                      <span className="detail-value">{detailModal.application.visa_name}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Category</span>
                      <span className="detail-value">{detailModal.application.visa_category || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Entries Allowed</span>
                      <span className="detail-value">{detailModal.application.entries_allowed ? detailModal.application.entries_allowed.charAt(0).toUpperCase() + detailModal.application.entries_allowed.slice(1) : '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Travel Start Date</span>
                      <span className="detail-value">{formatDate(detailModal.application.travel_start_date)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Travel End Date</span>
                      <span className="detail-value">{formatDate(detailModal.application.travel_end_date)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Submitted At</span>
                      <span className="detail-value">{formatDate(detailModal.application.submitted_at)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Paid At</span>
                      <span className="detail-value">{formatDate(detailModal.application.paid_at)}</span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3 className="detail-section-title">Payment Info</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Total Amount</span>
                      <span className="detail-value detail-value-highlight">
                        {formatCurrency(detailModal.application.total_amount, detailModal.application.currency)}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Payment Status</span>
                      <span className={`detail-payment-status payment-${detailModal.application.payment_status || 'pending'}`}>
                        {(detailModal.application.payment_status || 'pending').toUpperCase()}
                      </span>
                    </div>
                    {detailModal.transactions && detailModal.transactions.length > 0 && (
                      <div className="detail-item">
                        <span className="detail-label">Payment Mode</span>
                        <span className="detail-value" style={{textTransform: 'capitalize'}}>{detailModal.transactions[0].payment_mode || '-'}</span>
                      </div>
                    )}
                    <div className="detail-item">
                      <span className="detail-label">Embassy Fees</span>
                      <span className="detail-value">{formatCurrency(detailModal.application.government_fee, detailModal.application.currency)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Naytive Service Fees</span>
                      <span className="detail-value">{formatCurrency(detailModal.application.service_fee, detailModal.application.currency)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Partner Premium</span>
                      <span className="detail-value">{formatCurrency(detailModal.application.partner_premium, detailModal.application.currency)}</span>
                    </div>
                  </div>
                </div>

                {detailModal.application.agency_name && (
                  <div className="detail-section">
                    <h3 className="detail-section-title">Agency Info</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="detail-label">Agency Name</span>
                        <span className="detail-value">{detailModal.application.agency_name}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Agency Code</span>
                        <span className="detail-value">{detailModal.application.agency_code}</span>
                      </div>
                      {detailModal.application.agency_email && (
                        <div className="detail-item">
                          <span className="detail-label">Agency Email</span>
                          <span className="detail-value">{detailModal.application.agency_email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {detailModal.groupTravelers && detailModal.groupTravelers.length > 1 && (
                  <div className="detail-section">
                    <h3 className="detail-section-title">Travellers ({detailModal.groupTravelers.length})</h3>
                    <div className="documents-list">
                      {detailModal.groupTravelers.map((tr, ti) => (
                        <div key={tr.id || ti} className="document-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                            <div className="document-info">
                              <span className="document-name">
                                {tr.name || 'Unnamed traveller'}
                                <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: 600, color: tr.isPrimary ? '#2540D9' : '#64748B' }}>
                                  {tr.isPrimary ? 'PRIMARY' : (tr.relationship || 'traveller').toUpperCase()}
                                </span>
                              </span>
                              <div className="document-meta">
                                <span className="document-status">{tr.applicationNumber}</span>
                                {tr.status && <span className={`document-status extraction-${tr.status}`}>{String(tr.status).toUpperCase()}</span>}
                              </div>
                            </div>
                            {tr.photoId && (
                              <button
                                className="document-view-btn document-download-btn"
                                onClick={async () => {
                                  try {
                                    const res = await fetch(`${process.env.REACT_APP_API_URL}/visa-applications/photos/${tr.photoId}/view`, { credentials: 'include' });
                                    const blob = await res.blob();
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `photo-${tr.name || tr.applicationNumber || tr.photoId}.jpeg`;
                                    a.click();
                                    URL.revokeObjectURL(url);
                                  } catch (err) { alert('Failed to download photo'); }
                                }}
                              >
                                PHOTO
                              </button>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '4px' }}>
                            {tr.documents && tr.documents.length > 0 ? (
                              tr.documents.map((doc, di) => (
                                <div key={doc.id || di} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', fontSize: '13px' }}>
                                  <span style={{ color: '#334155' }}>{doc.document_name || doc.file_name}</span>
                                  {doc.id && (
                                    <span style={{ display: 'flex', gap: '6px' }}>
                                      <button className="document-view-btn" onClick={() => handleViewDocument(doc.id)}>VIEW</button>
                                      <button className="document-view-btn document-download-btn" onClick={() => handleDownloadDocument(doc.id, doc.file_name || doc.document_name)}>DOWNLOAD</button>
                                    </span>
                                  )}
                                </div>
                              ))
                            ) : (
                              <span style={{ fontSize: '12px', color: '#EF4444' }}>⚠ No passport document on file</span>
                            )}
                            {!tr.photoId && <span style={{ fontSize: '12px', color: '#EF4444' }}>⚠ No photo on file</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(!detailModal.groupTravelers || detailModal.groupTravelers.length <= 1) && detailModal.processedPhoto && (
                  <div className="detail-section">
                    <h3 className="detail-section-title">Passport Size Photo</h3>
                    <div className="processed-photo-container">
                      {photoUrl ? (
                        <img
                          src={photoUrl}
                          alt="Passport Size Photo"
                          className="processed-photo-img"
                        />
                      ) : (
                        <div className="photo-loading">Loading photo...</div>
                      )}
                      <div className="processed-photo-meta">
                        <span className={`document-status extraction-${detailModal.processedPhoto.status}`}>
                          {detailModal.processedPhoto.status.toUpperCase()}
                        </span>
                        {detailModal.processedPhoto.dimensions && (
                          <span className="photo-dimensions">{detailModal.processedPhoto.dimensions}px</span>
                        )}
                        <button
                          className="document-view-btn"
                          style={{ marginTop: '4px' }}
                          onClick={async () => {
                            try {
                              const res = await fetch(`${process.env.REACT_APP_API_URL}/visa-applications/photos/${detailModal.processedPhoto.id}/view`, { credentials: 'include' });
                              const blob = await res.blob();
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `passport-photo-${detailModal.application?.application_number || detailModal.processedPhoto.id}.jpeg`;
                              a.click();
                              URL.revokeObjectURL(url);
                            } catch (err) {
                              alert('Failed to download photo');
                            }
                          }}
                        >
                          DOWNLOAD
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {(!detailModal.groupTravelers || detailModal.groupTravelers.length <= 1) && detailModal.documents && detailModal.documents.length > 0 && (
                  <div className="detail-section">
                    <h3 className="detail-section-title">Documents ({detailModal.documents.length})</h3>
                    <div className="documents-list">
                      {detailModal.documents.map((doc, index) => (
                        <div key={doc.id || index} className="document-item">
                          <div className="document-icon">
                            {doc.file_mime_type?.includes('pdf') ? (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                              </svg>
                            ) : (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                <polyline points="21 15 16 10 5 21"></polyline>
                              </svg>
                            )}
                          </div>
                          <div className="document-info">
                            <span className="document-name">{doc.document_name || doc.file_name}</span>
                            <div className="document-meta">
                              <span className={`document-status extraction-${doc.extraction_status || 'pending'}`}>
                                {(doc.extraction_status || 'pending').toUpperCase()}
                              </span>
                              {doc.verification_status && (
                                <span className={`document-status verification-${doc.verification_status}`}>
                                  {doc.verification_status.toUpperCase()}
                                </span>
                              )}
                            </div>
                          </div>
                          {doc.id && (
                            <div className="document-actions">
                              <button
                                className="document-view-btn"
                                onClick={() => handleViewDocument(doc.id)}
                              >
                                VIEW
                              </button>
                              <button
                                className="document-view-btn document-download-btn"
                                onClick={() => handleDownloadDocument(doc.id, doc.file_name || doc.document_name)}
                              >
                                DOWNLOAD
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {detailModal.statusHistory && detailModal.statusHistory.length > 0 && (
                  <div className="detail-section">
                    <h3 className="detail-section-title">Status History</h3>
                    <div className="history-timeline">
                      {detailModal.statusHistory.map((item, index) => (
                        <div key={index} className="history-item">
                          <div className={`history-dot status-dot-${item.new_status}`}></div>
                          <div className="history-content">
                            <div className="history-header">
                              <span className={`history-status status-badge-${item.new_status}`}>
                                {(item.new_status || '').replace('_', ' ').toUpperCase()}
                              </span>
                              <span className="history-date">{formatDate(item.changed_at)}</span>
                            </div>
                            {item.remarks && (
                              <p className="history-remarks">{item.remarks}</p>
                            )}
                            {item.changed_by_name && (
                              <p className="history-remarks">By: {item.changed_by_name}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Admin Comments */}
                <div className="detail-section">
                  <h3 className="detail-section-title">
                    Admin Comments {detailModal.comments && detailModal.comments.length > 0 ? `(${detailModal.comments.length})` : ''}
                  </h3>
                  {detailModal.comments && detailModal.comments.length > 0 && (
                    <div className="comments-list">
                      {detailModal.comments.map((c) => (
                        <div key={c.id} className="comment-item">
                          <div className="comment-header">
                            <span className="comment-author">{c.admin_name}</span>
                            <span className="comment-time">{formatDate(c.created_at)}</span>
                            <button className="comment-delete-btn" onClick={() => handleDeleteComment(c.id)} title="Delete comment">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              </svg>
                            </button>
                          </div>
                          <p className="comment-text">{c.comment}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="comment-compose">
                    <textarea
                      className="comment-input"
                      placeholder="Add a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                      rows={2}
                    />
                    <button
                      className="comment-submit-btn"
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || commentLoading}
                    >
                      {commentLoading ? 'Adding...' : 'Add Comment'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Email Modal */}
      {emailModal.isOpen && (
        <div className="modal-overlay" onClick={() => setEmailModal({ isOpen: false, subject: '', body: '', sending: false, appId: null, appEmail: '' })}>
          <div className="email-modal" onClick={(e) => e.stopPropagation()}>
            <div className="email-modal-header">
              <h3>Email Applicant</h3>
              <button className="modal-close" onClick={() => setEmailModal({ isOpen: false, subject: '', body: '', sending: false, appId: null, appEmail: '' })}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="email-modal-body">
              <div className="email-field">
                <label className="email-label">To</label>
                <input className="email-input" type="text" value={emailModal.appEmail} readOnly />
              </div>
              <div className="email-field">
                <label className="email-label">Subject</label>
                <input
                  className="email-input"
                  type="text"
                  placeholder="Email subject"
                  value={emailModal.subject}
                  onChange={(e) => setEmailModal(prev => ({ ...prev, subject: e.target.value }))}
                />
              </div>
              <div className="email-field">
                <label className="email-label">Body</label>
                <textarea
                  className="email-textarea"
                  placeholder="Write your message..."
                  value={emailModal.body}
                  onChange={(e) => setEmailModal(prev => ({ ...prev, body: e.target.value }))}
                  rows={6}
                />
              </div>
            </div>
            <div className="email-modal-footer">
              <button className="email-cancel-btn" onClick={() => setEmailModal({ isOpen: false, subject: '', body: '', sending: false, appId: null, appEmail: '' })}>Cancel</button>
              <button
                className="email-send-btn"
                onClick={handleSendEmail}
                disabled={!emailModal.subject.trim() || !emailModal.body.trim() || emailModal.sending}
              >
                {emailModal.sending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Confirmation Modal */}
      {statusConfirm && (
        <div className="wa-confirm-overlay" onClick={() => setStatusConfirm(null)}>
          <div className="wa-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wa-confirm-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 className="wa-confirm-title">Change Application Status?</h3>
            <p className="wa-confirm-text">
              Change <strong>{statusConfirm.appNumber}</strong> from <strong>{getStatusConfig(statusConfirm.currentStatus).label}</strong> to <strong>{getStatusConfig(statusConfirm.newStatus).label}</strong>?
            </p>
            <div className="wa-confirm-actions">
              <button className="wa-confirm-skip" onClick={() => setStatusConfirm(null)}>
                Cancel
              </button>
              <button className="wa-confirm-send" style={{ background: '#2188C9' }} onClick={confirmStatusChange}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Confirmation Modal */}
      {whatsAppConfirm && (
        <div className="wa-confirm-overlay" onClick={() => {
          executeStatusChange(whatsAppConfirm.appId, whatsAppConfirm.newStatus, false);
        }}>
          <div className="wa-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wa-confirm-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" fill="#25D366"/>
                <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" stroke="#25D366" strokeWidth="1.5" fill="none"/>
              </svg>
            </div>
            <h3 className="wa-confirm-title">Send WhatsApp Notification?</h3>
            <p className="wa-confirm-text">
              Notify <strong>{whatsAppConfirm.applicantName}</strong> about visa {whatsAppConfirm.newStatus === 'approved' ? 'approval' : 'rejection'} via WhatsApp?
            </p>
            <div className="wa-confirm-actions">
              <button
                className="wa-confirm-skip"
                onClick={() => executeStatusChange(whatsAppConfirm.appId, whatsAppConfirm.newStatus, false)}
              >
                No, Skip
              </button>
              <button
                className="wa-confirm-send"
                onClick={() => executeStatusChange(whatsAppConfirm.appId, whatsAppConfirm.newStatus, true)}
              >
                Yes, Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisaApplications;
